import { passwordRequirementTooltip } from '@/components';
import { AuthContext } from '@/contexts/authContext';
import { useParseQueryString } from '@/hooks';
import { useGetTenantQuery } from '@/hooks/useGetTenantQuery';
import { useResetPasswordChallenge } from '@/queries/auth.query';
import { PRIVATE_ROUTES, PUBLIC_ROUTES } from '@/routes';
import { CustomError, errorNotification } from '@/utils';
import {
  INVALID_FIELDS,
  passwordPatternChecking,
  YupString,
} from '@/validations';
import { ControlledInput, CustomButton } from '@amili-v3/amili-v3-design';
import { useMobile } from '@amili-v3/amili-v3-design/src/hooks/useMobile';
import { notification } from 'antd';
import { FormikValues } from 'formik';
import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import AuthCardHeader from '../AuthCardHeader';
import { authPasswordToggle } from '../authPasswordToggle';
import { AuthModal } from '../styles';

const validationSchema = Yup.object().shape({
  password: YupString.matches(
    passwordPatternChecking,
    INVALID_FIELDS.PASSWORD_PATTERN,
  ),
  passwordConfirm: Yup.string()
    .oneOf([Yup.ref('password')], INVALID_FIELDS.PASSWORD_MATCH)
    .required(INVALID_FIELDS.REQUIRED),
});

const ChallengeResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const { setAuth } = useContext(AuthContext);
  const isMobile = useMobile();
  const { tenant } = useGetTenantQuery();
  const { token } = useParseQueryString<{ token?: string }>();

  const mutationResetPassword = useResetPasswordChallenge({
    onSuccess: res => {
      const authenticationResult = res.data?.authenticationResult;
      if (!authenticationResult?.accessToken) {
        notification.error({ message: t('message.signInFailed') });
        return;
      }
      setAuth(authenticationResult);
      notification.success({ message: t('message.signInSuccess') });
      navigate(isMobile ? PRIVATE_ROUTES.ORDER.ROOT : PRIVATE_ROUTES.APP);
    },
    onError: error =>
      errorNotification({ error: error as unknown as CustomError }),
  });

  const handleResetPassword = (value: FormikValues) => {
    if (!token || !tenant?.subdomain) {
      notification.error({ message: t('authentication.sessionExpired') });
      return;
    }

    return mutationResetPassword.mutateAsync({
      password: value.password as string,
      token,
      subdomain: tenant.subdomain,
    });
  };

  return (
    <AuthModal
      centered
      open={open}
      mask={false}
      width={420}
      hideCloseButton
      layout="vertical"
      footerBorder={false}
      title={<AuthCardHeader heading={t('authentication.resetPassword')} />}
      goBack={() => {
        setOpen(false);
        setTimeout(() => {
          navigate(PUBLIC_ROUTES.SIGN_IN);
        }, 200);
      }}
      formikProps={{
        validationSchema,
        onSubmit: handleResetPassword,
        initialValues: { password: '', passwordConfirm: '' },
      }}
      footer={[
        <CustomButton
          size="large"
          width="100%"
          key="submit"
          form="modal-id"
          htmlType="submit"
          loading={mutationResetPassword.isPending}
          aria-label="Submit btn"
        >
          {t('common.submit')}
        </CustomButton>,
      ]}
    >
      <ControlledInput
        required
        name="password"
        inputType="password"
        passwordIconRender={authPasswordToggle}
        label={t('common.password')}
        role="textbox"
        customFormProps={{ tooltip: passwordRequirementTooltip }}
        aria-label="Password inp"
      />
      <ControlledInput
        required
        role="textbox"
        inputType="password"
        passwordIconRender={authPasswordToggle}
        name="passwordConfirm"
        label={t('authentication.confirmPassword')}
        aria-label="Confirm Password inp"
      />
    </AuthModal>
  );
};

export default ChallengeResetPassword;
